import {
  OBAddress,
  OBGenericSensor,
  OBSensor,
  OBSensorConfigurations,
  OBSensorInfo,
  OBSensorNetwork,
  OBSensorShipping,
} from '../../../../../types';
import { apiRawFetch } from '../../../../../APIClient';

export type SensorFormValues = Omit<OBSensor, 'shippingSiteLocation'> &
  Omit<OBAddress, 'id'>;
export type SensorFormInfoValues = OBSensorInfo;
export type SensorFormNetworkValues = OBSensorNetwork;
export type SensorFormShippingValues = Omit<
  OBSensorShipping,
  'shippingSiteLocation'
> &
  Omit<OBAddress, 'id'>;

export const EMPTY_SENSOR: SensorFormValues = {
  name: '',
  model: '',
  series: '',
  siteId: '',

  isIPAutoConfig: false,
  ipAddress: '',
  subnetMask: '',
  gateway: '',
  primaryDns: '',
  secondaryDns: '',

  packetConfig: OBSensorConfigurations.SPAN_MIRROR,
  currentCapabilities: {},
  recommendationOverridden: false,

  isShippingToNewSite: false,
  shippingSiteId: '',
  country: '',
  street: '',
  street2: '',
  city: '',
  region: '',
  postcode: '',

  isShippingToNewContact: false,
  shippingContactId: '',
  shippingContactFirstName: '',
  shippingContactLastName: '',
  shippingContactEmail: '',
  shippingContactPhoneNumber: '',
  shippingContactPhoneType: 'office',

  shippingNotes: '',
  configurationTaskID: 0,
};

export const getFormValues = (sensor: OBSensor): SensorFormValues => ({
  name: sensor.name,
  model: sensor.model,
  series: sensor.series,
  siteId: sensor.siteId,

  isIPAutoConfig: sensor.isIPAutoConfig,
  ipAddress: sensor.ipAddress,
  subnetMask: sensor.subnetMask,
  gateway: sensor.gateway,
  primaryDns: sensor.primaryDns,
  secondaryDns: sensor.secondaryDns,

  packetConfig: sensor.packetConfig,
  currentCapabilities: sensor.currentCapabilities,
  recommendationOverridden: sensor.recommendationOverridden,

  isShippingToNewSite: sensor.shippingSiteLocation.id === '' || false,
  shippingSiteId: sensor.shippingSiteLocation.id,
  country: sensor.shippingSiteLocation.country,
  street: sensor.shippingSiteLocation.street,
  street2: sensor.shippingSiteLocation.street2,
  city: sensor.shippingSiteLocation.city,
  region: sensor.shippingSiteLocation.region,
  postcode: sensor.shippingSiteLocation.postcode,

  isShippingToNewContact: sensor.isShippingToNewContact,
  shippingContactId: sensor.shippingContactId,
  shippingContactFirstName: sensor.shippingContactFirstName,
  shippingContactLastName: sensor.shippingContactLastName,
  shippingContactEmail: sensor.shippingContactEmail,
  shippingContactPhoneNumber: sensor.shippingContactPhoneNumber,
  shippingContactPhoneType: sensor.shippingContactPhoneType,

  shippingNotes: sensor.shippingNotes,
  configurationTaskID: sensor.configurationTaskID,
});

export const getSensorModelFromFormValues = (
  values: SensorFormValues,
): OBSensor => {
  const { country, street, street2, city, region, postcode, ...restSensor } =
    values;
  return {
    ...restSensor,
    shippingSiteLocation: {
      id: values.isShippingToNewSite ? '' : values.shippingSiteId,
      country,
      street,
      street2,
      city,
      region,
      postcode,
    },
    ipAddress: values.ipAddress.trim(),
    subnetMask: values.subnetMask.trim(),
    gateway: values.gateway.trim(),
  };
};

export const getSensorInfoValues = (
  sensor: SensorFormValues,
): SensorFormInfoValues => ({
  name: sensor.name,
  model: sensor.model,
  series: sensor.series,
  siteId: sensor.siteId,
  recommendationOverridden: sensor.recommendationOverridden,
  modelChanged: sensor.modelChanged,
  configurationTaskID: sensor.configurationTaskID,
});

export const getSensorNetworkValues = (
  sensor: SensorFormValues,
): SensorFormNetworkValues => ({
  isIPAutoConfig: sensor.isIPAutoConfig,
  ipAddress: sensor.ipAddress,
  subnetMask: sensor.subnetMask,
  gateway: sensor.gateway,
  primaryDns: sensor.primaryDns,
  secondaryDns: sensor.secondaryDns,

  packetConfig: sensor.packetConfig,
  currentCapabilities: sensor.currentCapabilities,
});

export const getSensorShippingValues = (
  sensor: SensorFormValues,
): SensorFormShippingValues => ({
  isShippingToNewSite: sensor.isShippingToNewSite,
  shippingSiteId: sensor.shippingSiteId,
  country: sensor.country,
  street: sensor.street,
  street2: sensor.street2,
  city: sensor.city,
  region: sensor.region,
  postcode: sensor.postcode,

  isShippingToNewContact: sensor.isShippingToNewContact,
  shippingContactId: sensor.shippingContactId,
  shippingContactFirstName: sensor.shippingContactFirstName,
  shippingContactLastName: sensor.shippingContactLastName,
  shippingContactEmail: sensor.shippingContactEmail,
  shippingContactPhoneNumber: sensor.shippingContactPhoneNumber,
  shippingContactPhoneType: sensor.shippingContactPhoneType,

  shippingNotes: sensor.shippingNotes,
});

export const BAD_MODEL_RECOMMENDATIONS = ['N/A', 'None found'];

export const fetchRecommendedModel = async (
  sensor: OBSensor | undefined,
): Promise<string> => {
  if (!sensor) {
    return 'N/A';
  }
  const response = await apiRawFetch.getRecommendedModel(
    sensor.series ?? '',
    sensor.currentCapabilities,
  );
  if (response && response.compatible.length > 0) {
    return response.compatible[0];
  } else {
    return 'None found';
  }
};

export const getSeriesLabel = (
  allSeries: OBGenericSensor[] | undefined,
  value: string,
) =>
  !!allSeries
    ? allSeries.find((sensor) => sensor.value === value)?.label ?? value
    : value;
